import { collectionGroup, query, where } from 'firebase/firestore'
import { useMemo } from 'react'
import { db } from '../libs/Firebase'
import { Enrollment } from '../../../pure-js/types/GrooverTypes'
import { useFirestoreQuery } from './useFirestoreQuery'
import { usePortalHostQueryGate } from './portalHostQueryGate'

/**
 * Portal: all enrollments where the user is main or partner (collection group queries, merged).
 *
 * @param enrollmentUserId - The enrollment user ID from usePortalAuth, or null if not authenticated
 * @param clientId - Optional client to filter to (from useClientRouteHost()). When provided, adds a
 *   `where('clientId', '==', clientId)` filter to both queries — used by client-specific Portal
 *   subdomains (AC-1, COU-334). Requires composite indexes (mainUser.id, clientId) and
 *   (partnerUser.id, clientId) on the enrollments collection group; see firestore.indexes.json.
 *   Omitted/undefined for the unified portal, which shows all clients' data.
 *   Queries are held until hostname resolution settles so a client subdomain does
 *   not briefly run the unfiltered unified-portal query.
 */
export const useEnrollmentsForPortalUser = (enrollmentUserId: string | null, clientId?: string) => {
  const { readyUserId, hostLoading } = usePortalHostQueryGate(enrollmentUserId)

  const qMain = useMemo(() => {
    if (!readyUserId) {
      return query(collectionGroup(db, 'dummy-enrollments'))
    }
    const constraints = [where('mainUser.id', '==', readyUserId)]
    if (clientId) {
      constraints.push(where('clientId', '==', clientId))
    }
    return query(collectionGroup(db, 'enrollments'), ...constraints)
  }, [readyUserId, clientId])

  const qPartner = useMemo(() => {
    if (!readyUserId) {
      return query(collectionGroup(db, 'dummy-enrollments'))
    }
    const constraints = [where('partnerUser.id', '==', readyUserId)]
    if (clientId) {
      constraints.push(where('clientId', '==', clientId))
    }
    return query(collectionGroup(db, 'enrollments'), ...constraints)
  }, [readyUserId, clientId])

  const main = useFirestoreQuery<Enrollment[]>(qMain, {
    fnName: 'useEnrollmentsForPortalUser-main',
    enabled: !!readyUserId
  })

  const partner = useFirestoreQuery<Enrollment[]>(qPartner, {
    fnName: 'useEnrollmentsForPortalUser-partner',
    enabled: !!readyUserId
  })

  const data = useMemo(() => {
    const map = new Map<string, Enrollment>()
    for (const e of main.data ?? []) {
      map.set(e.id, e)
    }
    for (const e of partner.data ?? []) {
      map.set(e.id, e)
    }
    return [...map.values()]
  }, [main.data, partner.data])

  const isLoading = hostLoading || main.isLoading || partner.isLoading
  const error = main.error ?? partner.error

  return { data, isLoading, error, status: isLoading ? 'loading' : error ? 'error' : 'success' } as const
}
